import React from 'react';
import PropTypes from 'prop-types';
import {FormFeedback, Input, InputGroup, InputGroupAddon} from "reactstrap";
import {useTranslation} from "react-i18next";
import Button from "components/Button";
import validateRegistrationNumber from "../../utils/Vehicle/validateRegistrationNumber";
import classnames from "classnames";
import useAfterUpdateEffect from "../../utils/useAfterUpdateEffect";
import apiPlatform from "../../apiPlatform";
import {omitBy} from "lodash";
import moment from "moment";
import {useDispatch} from "react-redux";
import {workshopPlanVehicleByRegNumberCountUpdate} from "../../reducers/workshop/actions";
import {showModal} from "../VehicleByRegNumber/VehicleByRegNumberModal/reducer/ModalActions";
import {toast} from "react-toastify";

const VehicleRegistrationNumberInput = props => {
  const { t } = useTranslation('vehicle_registration_number_input');
  const dispatch = useDispatch()
  const [fetchingVehicleData, setFetchingVehicleData] = React.useState(false)
  const [vehicleDataFetched, setVehicleDataFetched] = React.useState(false)
  const [registrationNumber, setRegistrationNumber] = React.useState(props.registrationNumber)
  const [probablyInvalid, setProbablyInvalid] = React.useState(false)
  const [vehicleDataReadyToFetch, setVehicleDataReadyToFetch] = React.useState(false)

  useAfterUpdateEffect(() => {
    props.onChange(registrationNumber)
    setVehicleDataFetched(false)
    setProbablyInvalid(false)
    setVehicleDataReadyToFetch(false)
    if(validateRegistrationNumber(registrationNumber)){
      setVehicleDataReadyToFetch(true)
    }
  }, [registrationNumber])

  const fetchVehicleDataByRegistrationNumber = () => {
    setFetchingVehicleData(true)
    setVehicleDataFetched(true)

    const toastLongRequest = setTimeout(() => {
      toast.info('Niektóre pojazdy system pobiera trochę dłużej, proszę czekać')
    }, 7000)

    apiPlatform.vehicles.fetchByRegistrationNumber(registrationNumber)
      .then(({data}) => {
        const vehicle = data.vehicle
        dispatch(workshopPlanVehicleByRegNumberCountUpdate(data.vehicleByRegNumberCount))
        props.onFetchVehicleData(omitBy({
          carMake: vehicle.carMake,
          carModel: vehicle.carModel,
          color: vehicle.color,
          brand: vehicle.brand,
          model: vehicle.model,
          capacity: vehicle.capacity,
          engine: vehicle.engine,
          enginePower: vehicle.enginePower,
          enginePowerUnit: vehicle.enginePowerUnit,
          productionDate: vehicle.productionDate,
          firstRegistration: vehicle.firstRegistration ? moment(vehicle.firstRegistration).format('YYYY-MM-DD') : null,
          insuranceEnd: vehicle.insuranceEnd ? moment(vehicle.insuranceEnd).format('YYYY-MM-DD') : null,
          technicalInspectionEnd: vehicle.technicalInspectionEnd ? moment(vehicle.technicalInspectionEnd).format('YYYY-MM-DD') : null,
          registrationNumber: vehicle.registrationNumber,
          vin: vehicle.vin,
        }, v => !v))

        setVehicleDataReadyToFetch(true)
      })
      .catch(res => {
        if(res.response?.status === 403){
          dispatch(showModal())
          toast.warning(res.response.data['hydra:description'])
        }
        setVehicleDataReadyToFetch(false)
      })
      .finally(() => {
        clearTimeout(toastLongRequest)
        setFetchingVehicleData(false)
      })
  }

  return <>
    <InputGroup>
      <Input
        className={classnames({
          'border-warning': probablyInvalid,
        })}
        disabled={fetchingVehicleData}
        invalid={!!props.errors}
        valid={vehicleDataReadyToFetch}
        onChange={e => setRegistrationNumber(e.target.value.replace(/[^0-9a-zA-Z]+/g, '').toUpperCase().substr(0, 12))}
        onBlur={() => {
          setVehicleDataReadyToFetch(false)
          if(registrationNumber) {
            const isValid = validateRegistrationNumber(registrationNumber)
            setProbablyInvalid(!isValid)

            if(isValid){
              setVehicleDataReadyToFetch(true)
            }
          }else{
            setProbablyInvalid(false)
          }
        }}
        placeholder={t('input_placeholder')}
        value={registrationNumber}
      />
      <InputGroupAddon addonType={'append'}>
        <Button
          loading={fetchingVehicleData}
          disabled={!validateRegistrationNumber(registrationNumber) || vehicleDataFetched}
          onClick={fetchVehicleDataByRegistrationNumber}
          tooltip={t('btn_tooltip')}
        >
          <i className="fas fa-search" />
        </Button>
      </InputGroupAddon>
    </InputGroup>
    {(vehicleDataReadyToFetch && !fetchingVehicleData && !vehicleDataFetched) && <FormFeedback className="d-block text-success cursor-pointer" onClick={fetchVehicleDataByRegistrationNumber}>Mamy dane tego pojazdu, kliknij aby je pobrać</FormFeedback>}
    {probablyInvalid && <FormFeedback className="d-block text-warning">{t('invalid_alert')}</FormFeedback>}
    {props.errors && <FormFeedback className="d-block">{props.errors?.join('; ')}</FormFeedback>}
  </>;
};

VehicleRegistrationNumberInput.defaultProps = {
  registrationNumber: '',
}

VehicleRegistrationNumberInput.propTypes = {
  registrationNumber: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onFetchVehicleData: PropTypes.func,
};

export default VehicleRegistrationNumberInput;